import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { ASSETS } from "@/content/assets";
import { SITE } from "@/content/site";

const LINES = ["Learn the World", "by Wandering Through It"];

export default function HomeHero() {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <section
      ref={ref}
      className="relative flex min-h-[96vh] items-end overflow-hidden bg-pineink"
      data-testid="home-hero"
    >
      <motion.div className="absolute inset-0" style={reduce ? {} : { y, scale }}>
        <img
          src={ASSETS.heroHome.src}
          alt={ASSETS.heroHome.alt}
          className="h-full w-full object-cover"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-black/40" aria-hidden="true" />
      </motion.div>

      <div className="container-x relative z-10 pb-24 pt-44 text-canvas sm:pb-32">
        <motion.p
          className="mb-8 flex items-center gap-4 text-xs font-semibold uppercase tracking-[0.24em] text-canvas/85"
          initial={reduce ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.15 }}
        >
          <span className="inline-block h-px w-10 bg-[#CFC286]" aria-hidden="true" />
          {SITE.name}
        </motion.p>

        <h1 className="max-w-4xl text-[2.6rem] font-medium leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={reduce ? false : { y: "112%" }}
                animate={reduce ? {} : { y: 0 }}
                transition={{ duration: 1.05, delay: 0.3 + i * 0.16, ease: [0.22, 1, 0.36, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          className="mt-8 max-w-xl text-lg leading-relaxed text-canvas/85"
          initial={reduce ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85 }}
        >
          Inspired by the dragonfly that crosses continents, we craft transformative, place-based
          experiences - where curiosity is the compass and the world is the classroom.
        </motion.p>

        <motion.div
          className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4"
          initial={reduce ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
        >
          <Link
            to="/journeys"
            data-testid="hero-explore-journeys-cta"
            className="inline-flex items-center gap-2 bg-canvas px-7 py-3.5 text-base font-medium text-pineink transition-colors duration-300 hover:bg-white"
          >
            Explore Our Journeys
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
          <Link
            to="/our-approach"
            data-testid="hero-our-approach-link"
            className="text-base font-medium text-canvas underline decoration-canvas/40 underline-offset-8 transition-colors duration-300 hover:decoration-canvas"
          >
            Our Approach
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
